import { useEffect, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGE_MIME_TYPES } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/UserAvatar";
import { uploadAsset } from "@/lib/uploadAsset";
import { captureException } from "@/lib/analytics";
import { useAuthStore } from "@/store/authStore";
import { getUserInitials } from "@/store/teamsStore";

/**
 * Inline editor for the caller's own profile: picture upload via the asset
 * presigned-URL flow, and first/last name editing.
 */
export function ProfileEditor() {
  const user = useAuthStore((state) => state.user);
  const updateProfile = useAuthStore((state) => state.updateProfile);

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [isSavingName, setIsSavingName] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
  }, [user?.firstName, user?.lastName]);

  if (!user) {
    return null;
  }

  const handlePictureSelect = async (file: File) => {
    setIsUploadingPicture(true);
    try {
      const asset = await uploadAsset(file);
      const saved = await updateProfile({ profilePictureAssetId: asset.id });
      if (!saved) {
        throw new Error("Failed to save profile picture");
      }
      toast.success("Profile picture updated");
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      );
      toast.error("Failed to update profile picture");
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleSaveName = async () => {
    const trimmedFirst = firstName.trim();
    if (!trimmedFirst) {
      toast.error("First name is required");
      return;
    }
    setIsSavingName(true);
    const saved = await updateProfile({
      firstName: trimmedFirst,
      lastName: lastName.trim(),
    });
    setIsSavingName(false);
    if (saved) {
      toast.success("Profile updated");
      return;
    }
    toast.error("Failed to update profile");
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative">
        <UserAvatar
          className="h-10 w-10"
          profilePictureUrl={user.profilePictureUrl}
          name="Profile picture"
          initials={getUserInitials(user)}
        />
        <Button
          size="icon"
          variant="secondary"
          className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border shadow-sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploadingPicture}
          aria-label="Change profile picture"
        >
          {isUploadingPicture ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Camera className="h-3 w-3" />
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_MIME_TYPES.join(",")}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              handlePictureSelect(file);
            }
            event.target.value = "";
          }}
        />
      </div>
      <Input
        className="w-28"
        placeholder="First name"
        value={firstName}
        onChange={(event) => setFirstName(event.target.value)}
      />
      <Input
        className="w-28"
        placeholder="Last name"
        value={lastName}
        onChange={(event) => setLastName(event.target.value)}
      />
      <Button
        size="sm"
        onClick={handleSaveName}
        disabled={isSavingName || !firstName.trim()}
      >
        Save
        {isSavingName && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
      </Button>
    </div>
  );
}
